import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/shop';
import { brand, formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import { useAuth } from '../auth/AuthContext';
import { MemberCardPanel } from '../components/MemberCardPanel';
import { ActivityList } from '../components/ActivityList';
import { PartnerBalances } from '../components/PartnerBalances';
import { FavoritesList } from '../components/FavoritesList';
import { NotificationSettings } from '../components/NotificationSettings';
import { SupportTickets } from '../components/SupportTickets';
import { InviteFriend } from '../components/InviteFriend';
import { PasswordSettings } from '../components/PasswordSettings';
import type { MyOrder, MyVoucher, WalletSummary } from '../api/types';

/**
 * SIX TABS IS THE CEILING, and this is at it.
 *
 * Everything §2.6 and §2.7 added is a customer-owned thing that belongs on this
 * page, but a seventh would push the wrapped strip to three rows on a phone. The
 * next feature that wants a home here needs a section inside an existing tab,
 * not a tab of its own.
 */
type Tab = 'vouchers' | 'activity' | 'orders' | 'card' | 'saved' | 'details';

/**
 * The buyer's dashboard.
 *
 * WHAT IT SHOWS AND WHY THOSE THINGS
 *
 * Three numbers at the top, and every one of them is computed from data the API
 * actually returns: credit still to spend, vouchers currently usable, and lifetime
 * spend. There is deliberately no "points", "tier" or "member since" — inventing
 * loyalty furniture on a page about somebody's money is the fastest way to make the
 * real numbers look invented too.
 *
 * TWO REQUESTS, NOT ONE. Vouchers and orders are separate endpoints and are loaded
 * in parallel; each renders as soon as it arrives and each fails on its own. A single
 * Promise.all would mean a slow order query hides vouchers the shopper needs at a
 * counter right now.
 *
 * NO VOUCHER CODES ANYWHERE ON THIS PAGE. The list endpoints do not return them, by
 * design — a dashboard is the page most likely to be left open on a shared screen.
 * The code lives one click away, on the voucher's own page.
 */
export function Account() {
  const { signOut } = useAuth();
  const [tab, setTab] = useState<Tab>('vouchers');

  const [vouchers, setVouchers] = useState<MyVoucher[] | null>(null);
  const [orders, setOrders] = useState<MyOrder[] | null>(null);
  const [summary, setSummary] = useState<WalletSummary | null>(null);

  usePageTitle('Your account');

  useEffect(() => {
    let cancelled = false;

    // Independent on purpose — see the note above.
    api.vouchers()
      .then((r) => { if (!cancelled) setVouchers(r); })
      .catch(() => { if (!cancelled) setVouchers([]); });

    api.orders()
      .then((r) => { if (!cancelled) setOrders(r); })
      .catch(() => { if (!cancelled) setOrders([]); });

    api.walletSummary()
      .then((r) => { if (!cancelled) setSummary(r); })
      .catch(() => { if (!cancelled) setSummary(null); });

    return () => { cancelled = true; };
  }, []);

  // THE HEADLINE FIGURES COME FROM THE SERVER, not from adding up the voucher list
  // in the browser. The list is capped, so a customer with a long history would have
  // been shown a total quietly missing everything past the cap — and two screens
  // adding the same numbers differently is how a customer stops believing either.
  //
  // The fallback is the old client-side sum, used only while the summary is in
  // flight or if it fails: a dash where a balance should be is worse than a figure
  // that is right for the vouchers actually on screen.
  const active = (vouchers ?? []).filter((v) => v.status === 'active');
  const credit = summary?.totalBalanceCents
    ?? active.reduce((sum, v) => sum + v.balanceCents, 0);
  const activeCount = summary?.activeVoucherCount ?? active.length;

  return (
    <>
      <section className="acct-hero">
        <div className="acct-hero-text">
          <p className="eyebrow">Your account</p>
          <h1>Everything you&rsquo;ve bought, in one place</h1>
          <p className="sub">
            Vouchers are ready to show at the counter — no printing, no app.
          </p>
        </div>

        {/* aria-live so the figures are announced when the two requests land,
            rather than silently replacing "—" for someone not looking at them. */}
        <div className="stats" aria-live="polite">
          <div className="stat">
            <span className="value">{vouchers === null ? '—' : formatMoney(credit)}</span>
            <span className="label">Credit to spend</span>
          </div>
          <div className="stat">
            <span className="value">{vouchers === null ? '—' : activeCount}</span>
            <span className="label">Active vouchers</span>
          </div>
          {/*
            SOW §2.7's savings tracker. It replaced "Total spent", which is the
            number a customer least enjoys being shown on their own dashboard and is
            still one tap away under Orders.

            Counted at PURCHASE, not as credit is spent — buying £50 of credit for
            £35 saved £15 that day, whether or not it has been used. The alternative
            makes the figure fall when a voucher lapses, which reads as the platform
            taking a saving back.
          */}
          <div className="stat">
            <span className="value">
              {summary === null ? '—' : formatMoney(summary.totalSavedCents)}
            </span>
            <span className="label">You&rsquo;ve saved</span>
          </div>
        </div>
      </section>

      <div className="tabcard acct-tabs">
        <div className="tablist" role="tablist" aria-label="Account sections">
          <button
            type="button" role="tab" id="tab-vouchers"
            aria-selected={tab === 'vouchers'} aria-controls="panel-vouchers"
            onClick={() => setTab('vouchers')}
          >
            Vouchers
          </button>
          <button
            type="button" role="tab" id="tab-activity"
            aria-selected={tab === 'activity'} aria-controls="panel-activity"
            onClick={() => setTab('activity')}
          >
            Activity
          </button>
          <button
            type="button" role="tab" id="tab-orders"
            aria-selected={tab === 'orders'} aria-controls="panel-orders"
            onClick={() => setTab('orders')}
          >
            {/* "Orders", not "Order history": three tabs have to share 320px
                without colliding, and the shorter word loses nothing. */}
            Orders
          </button>
          <button
            type="button" role="tab" id="tab-card"
            aria-selected={tab === 'card'} aria-controls="panel-card"
            onClick={() => setTab('card')}
          >
            Card
          </button>
          <button
            type="button" role="tab" id="tab-saved"
            aria-selected={tab === 'saved'} aria-controls="panel-saved"
            onClick={() => setTab('saved')}
          >
            Saved
          </button>
          <button
            type="button" role="tab" id="tab-details"
            aria-selected={tab === 'details'} aria-controls="panel-details"
            onClick={() => setTab('details')}
          >
            Account
          </button>
        </div>

        {tab === 'vouchers' && (
          <div className="tabpanel" role="tabpanel" id="panel-vouchers" aria-labelledby="tab-vouchers">
            {/* Partners first, then the vouchers themselves. "How much have I got
                left at the Italian place" is the question people actually arrive
                with, and three vouchers for one restaurant used to mean three rows
                and mental arithmetic. */}
            <PartnerBalances summary={summary} />
            <VoucherList vouchers={vouchers} />
          </div>
        )}

        {tab === 'activity' && (
          <div className="tabpanel" role="tabpanel" id="panel-activity" aria-labelledby="tab-activity">
            <ActivityList />
          </div>
        )}

        {tab === 'orders' && (
          <div className="tabpanel" role="tabpanel" id="panel-orders" aria-labelledby="tab-orders">
            <OrderList orders={orders} />
          </div>
        )}

        {tab === 'card' && (
          <div className="tabpanel" role="tabpanel" id="panel-card" aria-labelledby="tab-card">
            {/* Mounted only while the tab is open, so the QR — which is
                spendable — is not sitting in the DOM of a page left on a desk. */}
            <MemberCardPanel />
          </div>
        )}

        {tab === 'saved' && (
          <div className="tabpanel" role="tabpanel" id="panel-saved" aria-labelledby="tab-saved">
            <FavoritesList />
          </div>
        )}

        {tab === 'details' && (
          <div className="tabpanel" role="tabpanel" id="panel-details" aria-labelledby="tab-details">
            {/* Answers "what is my password?" plainly, in both of its shapes — the
                account that has one and the account that does not. That question is
                the single most common support contact for any sign-in flow, and the
                one-time code is still the answer to "I forgot it". */}
            <PasswordSettings />

            {/* SOW §2.6. This replaced a mailto link, which had no record, no
                status, and no way to tell whether anyone had read it. */}
            <SupportTickets />

            {/* Kept alongside the ticket form rather than replaced by it: some
                people simply prefer email, and the terms link belongs with the
                thing it explains. */}
            <section className="stack" style={{ gap: 10 }}>
              <h2>Prefer email?</h2>
              <p>
                Write to <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>{' '}
                and we normally reply {brand.supportReplyTime}. Refunds and how
                they work are set out in the{' '}
                <Link to="/terms">terms of use</Link>.
              </p>
            </section>

            {/* SOW §2.7 */}
            <NotificationSettings />

            {/* SOW §2.7 — a share link only. NOT the rewarded referral layer,
                which §10.8 excludes from the base build. */}
            <InviteFriend />

            <section className="stack" style={{ gap: 10 }}>
              <h2>Sign out</h2>
              <p>
                Ends this session on this device. Your vouchers stay exactly as they
                are — sign back in to see them again.
              </p>
              <button type="button" className="btn secondary" onClick={() => void signOut()}>
                Sign out
              </button>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- vouchers --- */

function VoucherList({ vouchers }: { vouchers: MyVoucher[] | null }) {
  if (vouchers === null) {
    return <SkeletonRows label="Loading your vouchers" />;
  }

  if (vouchers.length === 0) {
    return (
      <Empty
        title="No vouchers yet"
        body="Vouchers you buy appear here straight after payment, ready to show at the counter."
      />
    );
  }

  return (
    <ul className="acct-list">
      {vouchers.map((v) => {
        const partly = v.balanceCents < v.faceValueCents;

        return (
          <li key={v.id}>
            <Link to={`/vouchers/${v.id}`} className="acct-row">
              <span className="acct-row-main">
                <span className="acct-row-title">{v.dealTitle}</span>
                <span className="acct-row-sub">
                  {v.businessName}
                  {v.lockedToBranch === null ? '' : ` · ${v.lockedToBranch}`}
                </span>
              </span>

              <span className="acct-row-side">
                <span className="acct-amount">{formatMoney(v.balanceCents)}</span>
                <span className="acct-row-sub">
                  {v.status === 'active'
                    ? `${partly ? `of ${formatMoney(v.faceValueCents)} · ` : ''}valid to ${formatDate(v.validTo)}`
                    : v.status === 'exhausted'
                      ? 'Fully used'
                      : v.status}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ orders --- */

function OrderList({ orders }: { orders: MyOrder[] | null }) {
  if (orders === null) {
    return <SkeletonRows label="Loading your orders" />;
  }

  if (orders.length === 0) {
    return (
      <Empty
        title="No orders yet"
        body="Once you buy something, every purchase and its receipt details show up here."
      />
    );
  }

  return (
    <ul className="acct-list">
      {orders.map((o) => (
        <li key={o.orderId}>
          {/* An order links to its VOUCHER when there is one, because that is what
              the customer actually wants from a past purchase. Without one — still
              pending, or failed — it links to the order page, which is the screen
              that can recover the payment. */}
          <Link
            to={o.voucherId === null ? `/orders/${o.orderId}` : `/vouchers/${o.voucherId}`}
            className="acct-row"
          >
            <span className="acct-row-main">
              <span className="acct-row-title">
                {o.dealTitle}
                {o.quantity > 1 && <span className="acct-qty"> ×{o.quantity}</span>}
              </span>
              <span className="acct-row-sub">
                {o.businessName} · {formatDate(o.paidAt ?? o.createdAt)}
              </span>
            </span>

            <span className="acct-row-side">
              <span className="acct-amount">{formatMoney(o.amountPaidCents)}</span>
              <OrderBadge status={o.status} />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * The status in the customer's words, not the database's.
 *
 * 'paid' is shown as "Voucher issued" because that is the thing they care about
 * having happened; 'pending' says what to do next rather than just naming a state.
 */
function OrderBadge({ status }: { status: MyOrder['status'] }) {
  const [text, tone] =
    status === 'paid' ? ['Voucher issued', 'ok']
      : status === 'pending' ? ['Payment not finished', 'warn']
        : status === 'refunded' ? ['Refunded', 'ok']
          : status === 'cancelled' ? ['Cancelled', 'warn']
            : ['Payment failed', 'warn'];

  return <span className={tone === 'ok' ? 'badge' : 'badge warn'}>{text}</span>;
}

/* ------------------------------------------------------------------ shared --- */

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="acct-empty">
      <h2>{title}</h2>
      <p className="muted">{body}</p>
      <Link to="/" className="btn">Browse offers</Link>
    </div>
  );
}

/**
 * role="status" with a visually hidden label: the bars are decorative, so without it
 * a screen reader reaches this panel and finds absolutely nothing to announce.
 */
function SkeletonRows({ label }: { label: string }) {
  return (
    <div role="status">
      <span className="visually-hidden">{label}</span>
      <ul className="acct-list" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <li key={i}>
            <div className="acct-row">
              <span className="acct-row-main">
                <span className="skeleton skeleton-line" style={{ width: '62%' }} />
                <span className="skeleton skeleton-line" style={{ width: '38%' }} />
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}
